import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { AppRoutes, getQueryStringValue } from '../../../app-routes';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { ITheme, IStyle, mergeStyleSets, getTheme, MessageBarType } from '@fluentui/react';
import { SteppedSectionList } from '../../components/step-list';
import { StepSectionState } from '../../components/step-section';
import { ITimeBlockListing, TimeBlockListing } from '../../../../entities-generated/models/time-block/time-block-listing';
import { CustomerAccountService, useProjectConfiguration } from '../../../../services/customer-account';
import { MyProjectApiClient } from '../../../../services/my-project';
import { PaymentApiClient } from '../../../../services/payment';
import { PendingTransactionStatus } from '../../../../entities-generated/common/pending-transaction-status';
import { IGetTransactionResultResponse } from '../../../../entities-generated/models/pending-transaction/get-transaction-result-response';
import { useNavigator } from '../../../../framework/services/navigation';
import { PaymentSectionAcceptTerms, TermsReviewState } from './sections/terms';
import { ShopInfoApiClient } from '../../../../services/shop-info';
import { TermsReviewPanel } from '../../components/terms-review-panel';
import { PaymentSectionInstallDate } from './sections/install';
import { PriceChangeSummary } from '../../components/price-change-summary';
import { BwjNote } from '../../../../framework/components/bwj-note';
import { SuccessGraphic } from '../../../../graphics/success';
import { BwjCheckbox } from '../../../../framework/components/bwj-checkbox';
import { CheckoutProductModalManager } from '../../components/product-modal-manager';
import { CheckoutSummary } from '../../components/checkout-summary';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { IStripeContact, PaymentSectionBillingAddress } from './sections/address';
import { useMainContentScrollReset } from '../../../app-content';
import { IBeginPurchaseRequest } from '../../../../entities-generated/models/pending-transaction/begin-purchase-request';
import { IBeginPurchaseResponse } from '../../../../entities-generated/models/pending-transaction/begin-purchase-response';
import { PaymentSectionCardInfo } from './sections/card-info';
import { DevProcessTransactionResultRequest } from '../../../../entities-generated/models/pending-transaction/dev-process-transaction-result-request';
import { StripeApiKey } from '../../../../services/stripe-api-key';
import { fireToggleLoadingOverlay, ShopAppEvents } from '../../../events';
import { EventNotificationService } from '../../../../framework/services/event-notification';
import { IAppHeaderConfiguration } from '../../../entities/app-header-config';

const getStyleClasses = (theme: ITheme) => {
  const main: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: '0px 30px',
    flexWrap: 'wrap-reverse',
  };

  const formMain: IStyle = {
    width: '100%',

    '@media(min-width: 992px)': {
      width: 1,
      flexGrow: 1,
    },

    '@media(min-width: 1200px)': {
      flexGrow: 2,
    },

    '@media(min-width: 1500px)': {
      flexGrow: 3,
    },
  };

  const formAdditional: IStyle = {
    width: '100%',

    '@media(min-width: 992px)': {
      width: 1,
      flexGrow: 1,
    },
  };

  const errorContainer: IStyle = {
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    margin: 0,
    marginBottom: '1.5rem',
  };

  return mergeStyleSets({
    main,
    formMain,
    formAdditional,
    errorContainer,
  });
};

enum PaymentErrorType {
  None,
  Retryable,
  Blocking,
}

interface IPaymentError {
  type: PaymentErrorType;
  message: string;
}

interface IStepListState {
  acceptTerms: StepSectionState;
  installDate: StepSectionState;
  billingAddress: StepSectionState;
  paymentInfo: StepSectionState;
}
const stepListState: IStepListState = {
  acceptTerms: StepSectionState.Active,
  installDate: StepSectionState.Untouched,
  billingAddress: StepSectionState.Untouched,
  paymentInfo: StepSectionState.Untouched
};

const updateHeaderDisplay = (showCart: boolean, showAccount: boolean) => {
  const config: IAppHeaderConfiguration = {
    doorSelectionTools: false,
    cartPanel: showCart,
    accountPanel: showAccount
  };
  EventNotificationService.dispatch(ShopAppEvents.UpdateHeaderConfiguration, config);
}

const devMode = StripeApiKey.startsWith('pk_test');

const subscriberId = 'Page.Checkout';

export const PageCheckout: React.FunctionComponent = () => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const nav = useNavigator();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('One moment please...');
  const errorState = useStandardErrorState();
  const [listState, setListState] = useState(stepListState);
  const paymentFailed = useRef(false);
  const availableInstallTimes = useRef<ITimeBlockListing[]>([]);
  const [selectedInstallTime, setSelectedInstallTime] = useState<ITimeBlockListing | undefined>(undefined);
  const projectConfig = useProjectConfiguration(subscriberId);
  const stripe = useRef(loadStripe(StripeApiKey));
  const [termsAcceptance, setTermsAcceptance] = useState(TermsReviewState.ReviewPending);
  const [showTermsPanel, setShowTermsPanel] = useState(false);
  const termsOfService = useRef('');
  const cancelAsyncOps = useRef(false);
  const [simulatePaymentFailure, setSimulatePaymentFailure] = useState(false);
  const billingAddress = useRef<IStripeContact | undefined>(undefined);
  const scrollToTop = useMainContentScrollReset();
  const [paymentError, setPaymentError] = useState<IPaymentError | undefined>(undefined);
  const payButtonClicked = useRef(false);

  const getRetryPaymentInstallDates = useCallback(() => {
    MyProjectApiClient.getProjectInstallationDateOptions()
    .then(resp => {
      // time block number 0 means 'schedule later'
      resp.push(new TimeBlockListing());
      availableInstallTimes.current = resp;
      setTermsAcceptance(TermsReviewState.Accepted);
      setViewState(BwjViewState.Ready);
      updateHeaderDisplay(true, true);
    })
    .catch(() => setViewState(BwjViewState.Error));
  }, []);

  const getConcretePaymentResults = useCallback((transactionId: string) => {
    return new Promise<IGetTransactionResultResponse>((resolve, reject) => {
      const checkForResults = () => {
        PaymentApiClient.getTransactionResult(transactionId)
        .then(resp => {
          if(resp.result !== PendingTransactionStatus.Pending) {
            resolve(resp);
          } else {
            window.setTimeout(checkForResults, 2000);
          }
        })
        .catch(e => reject(e));
      };
      checkForResults();
    });
  }, []);

  const loadWithPaymentResults = useCallback((transactionId: string) => {
    updateHeaderDisplay(false, false);

    ShopInfoApiClient.getTermsOfService()
    .then(tos => {
      if(cancelAsyncOps.current) { return undefined; }

      termsOfService.current = tos;
      return getConcretePaymentResults(transactionId);
    })
    .then(resp => {
      if(!resp || cancelAsyncOps.current) { return undefined; }

      CustomerAccountService.unsafeSetAccountTokenHeyAreYouSureYouKnowWhatYouAreDoing(resp.projectId);
      if(resp.result === PendingTransactionStatus.Successful) {
        updateHeaderDisplay(false, true);
        setViewState(BwjViewState.Custom);
      } else { // can only be failed; Pending not a possibility at this point
        paymentFailed.current = true;
        !!resp.projectConfiguration && CustomerAccountService.updateProjectConfiguration(resp.projectConfiguration);
        getRetryPaymentInstallDates();
      }
    })
    .catch((errCode) => {
      if(errCode === 404) {
        nav.goTo(AppRoutes.home.url());
      } else {
        setViewState(BwjViewState.Error);
      }
    });
  }, [getRetryPaymentInstallDates, getConcretePaymentResults, nav]);

  const loadNormal = useCallback(() => {
    ShopInfoApiClient.getTermsOfService()
    .then(tos => {
      if(cancelAsyncOps.current) { return undefined; }

      termsOfService.current = tos;
      return MyProjectApiClient.refreshPricing();
    })
    .then(resp => {
      if(!resp || cancelAsyncOps.current) { return undefined; }

      CustomerAccountService.updateProjectConfiguration(resp);
      return MyProjectApiClient.getProjectInstallationDateOptions();
    })
    .then(options => {
      if(!options || cancelAsyncOps.current) { return undefined; }

      options.push(new TimeBlockListing());
      availableInstallTimes.current = options;
      setViewState(BwjViewState.Ready);
    })
    .catch(() => setViewState(BwjViewState.Error));
  }, []);

  useEffect(() => {
    const transactionId = getQueryStringValue('transaction-id');
    if(!!transactionId) {
      loadWithPaymentResults(transactionId);
    } else {
      loadNormal();
    }

    return () => {
      cancelAsyncOps.current = true;
    }
  }, [loadNormal, loadWithPaymentResults]);

  const onEditTermsSection = () => {
    const ls = {...listState};
    ls.acceptTerms = StepSectionState.Active;
    ls.installDate = ls.installDate === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.installDate;
    ls.billingAddress = ls.billingAddress === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.billingAddress;
    ls.paymentInfo = ls.paymentInfo === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.paymentInfo;
    setListState(ls);
  };
  const onAdvanceToInstallDateSection = () => {
    const ls = {...listState};
    ls.acceptTerms = StepSectionState.EditableTouched;
    ls.installDate = StepSectionState.Active;
    setListState(ls);
  };
  const onAcceptanceChanged = (acceptance: TermsReviewState) => {
    const isTouched = (sss: StepSectionState) => {
      return sss === StepSectionState.EditableTouched
        || sss === StepSectionState.UneditableTouched;
    };
    const touchedState = acceptance === TermsReviewState.Accepted
      ? StepSectionState.EditableTouched : StepSectionState.UneditableTouched;

    const ls = {...listState};
    ls.installDate = isTouched(ls.installDate)
      ? touchedState : ls.installDate;
    ls.billingAddress = isTouched(ls.billingAddress)
      ? touchedState : ls.billingAddress;
    ls.paymentInfo = isTouched(ls.paymentInfo)
      ? touchedState : ls.paymentInfo;
    setListState(ls);
    setTermsAcceptance(acceptance);
  };

  const onDismissTermsPanel = () => {
    termsAcceptance === TermsReviewState.ReviewPending
      && setTermsAcceptance(TermsReviewState.Reviewed);
    setShowTermsPanel(false);
  }

  const onEditInstallDateSection = () => {
    const ls = {...listState};
    ls.installDate = StepSectionState.Active;
    ls.acceptTerms = ls.acceptTerms === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.acceptTerms;
    ls.billingAddress = ls.billingAddress === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.billingAddress;
    ls.paymentInfo = ls.paymentInfo === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.paymentInfo;
    setListState(ls);
  };
  const onAdvanceToBillingAddressSection = () => {
    const ls = {...listState};
    ls.installDate = StepSectionState.EditableTouched;
    ls.billingAddress = StepSectionState.Active;
    setListState(ls);
  };

  const onEditBillingAddressSection = () => {
    const ls = {...listState};
    ls.billingAddress = StepSectionState.Active;
    ls.acceptTerms = ls.acceptTerms === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.acceptTerms;
    ls.installDate = ls.installDate === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.installDate;
    ls.paymentInfo = ls.paymentInfo === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.paymentInfo;
    setListState(ls);
  };
  const onBillingAddressChanged = () => {
    const isTouched = (sss: StepSectionState) => {
      return sss === StepSectionState.EditableTouched
        || sss === StepSectionState.UneditableTouched;
    };
    const touchedState = StepSectionState.UneditableTouched;

    const ls = {...listState};
    ls.acceptTerms = touchedState;
    ls.installDate = touchedState;
    ls.paymentInfo = isTouched(ls.paymentInfo)
      ? touchedState : ls.paymentInfo;
    setListState(ls);
  };
  const onAdvanceToPaymentInfoSection = (contact: IStripeContact) => {
    const ls = {...listState};
    ls.acceptTerms = StepSectionState.EditableTouched;
    ls.installDate = StepSectionState.EditableTouched;
    ls.billingAddress = StepSectionState.EditableTouched;
    ls.paymentInfo = StepSectionState.Active;
    setListState(ls);
    billingAddress.current = contact;
  };

  const onEditCardInfoSection = () => {
    const ls = {...listState};
    ls.paymentInfo = StepSectionState.Active;
    ls.acceptTerms = ls.acceptTerms === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.acceptTerms;
    ls.installDate = ls.installDate === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.installDate;
    ls.billingAddress = ls.billingAddress === StepSectionState.Active
      ? StepSectionState.EditableTouched : ls.billingAddress;
    setListState(ls);
  };
  const onCardInfoChanged = () => {
    const touchedState = StepSectionState.UneditableTouched;
    const ls = {...listState};

    ls.acceptTerms = touchedState;
    ls.installDate = touchedState;
    ls.billingAddress = touchedState;
    setListState(ls);
  };

  const processPayment = (resp: IBeginPurchaseResponse, processor: Stripe, elements: StripeElements) => {
    if(devMode) {
      const devReq: DevProcessTransactionResultRequest = {
        transactionId: resp.transactionId,
        transactionSuccessful: simulatePaymentFailure === false
      };
      window.setTimeout(() => {
        PaymentApiClient.devProcessTransactionResult(devReq)
        .catch(e => console.log(e));
      }, 50);
    }

    processor.confirmPayment({
      elements,
      clientSecret: resp.clientSecret,
      confirmParams: {
        payment_method_data: {
          billing_details: billingAddress.current!,
        },
        return_url: `${window.location.origin}/checkout?transaction-id=${resp.transactionId}`,
      },
      redirect: 'always',
    })
    .then(resp => {
      if(resp.error) {
        paymentFailed.current = true;
        payButtonClicked.current = false;
      }
    })
    .catch((e) => {
      devMode && console.log('A Stripe error occurred:');
      devMode && console.log(e);
      const err: IPaymentError = {
        type: PaymentErrorType.Blocking,
        message: 'We&#39;re sorry &ndash; we encountered an error while processing your payment.  <strong>Please do not attempt to retry this payment.</strong>. Email us at immediately at <a href="mailto:customercare@bwjstorm.com">customercare@bwjstorm.com</a>, or during normal business hours, call us at <a href="tel:5855761654">(585) 576-1654</a>.'
      };
      setPaymentError(err);
    })
    .finally(() => {
      fireToggleLoadingOverlay(false);
      scrollToTop();
    });
  };

  const onCompletePayment = (elements: StripeElements | null) => {
    if(payButtonClicked.current) { return; } // prevent multiple submissions
    
    const ls = {...listState};
    ls.acceptTerms = StepSectionState.EditableTouched;
    ls.installDate = StepSectionState.EditableTouched;
    ls.billingAddress = StepSectionState.EditableTouched;
    setListState(ls);

    fireToggleLoadingOverlay(true, 'Processing payment...');
    setPaymentError(undefined);

    const request: IBeginPurchaseRequest = {
      invoiceId: CustomerAccountService.getAccountToken() ?? '',
      desiredInstallationTime: selectedInstallTime?.blockNumber ?? 0
    };
    PaymentApiClient.beginPurchase(request)
    .then(resp => {
      if(resp.duplicateRequest || resp.updatedConfiguration) {
        if(resp.duplicateRequest) {
          devMode && console.log('Duplicate transaction failure');
          const err: IPaymentError = {
            type: PaymentErrorType.Blocking,
            message: 'We&#39;re sorry &ndash; we encountered an error while processing your payment.  <strong>Please do not attempt to retry this payment.</strong>. Email us at immediately at <a href="mailto:customercare@bwjstorm.com">customercare@bwjstorm.com</a>, or during normal business hours, call us at <a href="tel:5855761654">(585) 576-1654</a>.'
          };
          setPaymentError(err);
        } else { // a price change occurred
          CustomerAccountService.updateProjectConfiguration(resp.updatedConfiguration!);
          payButtonClicked.current = false; // allow payment resubmission
        }
        fireToggleLoadingOverlay(false);
        scrollToTop();
      } else { // ready to make a purchase
        const handleStripeIssue = () => {
          const err: IPaymentError = {
            type: PaymentErrorType.Retryable,
            message: 'We&#39;re sorry &ndash; we&#39;re having issues with our payment system at the moment.  <strong>Your payment method was not charged.</strong>. Please try refreshing this page.  You may also contact us at <a href="mailto:customercare@bwjstorm.com">customercare@bwjstorm.com</a>, or during normal business hours, call <a href="tel:5855761654">(585) 576-1654</a>.'
          };
          setPaymentError(err);
          fireToggleLoadingOverlay(false);
          scrollToTop();
          payButtonClicked.current = false;
        }
        stripe.current
        .then(s => {
          if(!!s && !!elements) {
            processPayment(resp, s, elements);
          } else {
            handleStripeIssue();
          }
        })
        .catch(handleStripeIssue);
      }
    })
    .catch(() => {
      const err: IPaymentError = {
        type: PaymentErrorType.Retryable,
        message: 'We&#39;re sorry &ndash; we ran into an error on our end while processing your payment.  <strong>Your payment method was not charged.</strong>. Please try resubmitting your payment.  You may also contact us at <a href="mailto:customercare@bwjstorm.com">customercare@bwjstorm.com</a>, or during normal business hours, call <a href="tel:5855761654">(585) 576-1654</a>.'
      };
      payButtonClicked.current = false;
      setPaymentError(err);
      fireToggleLoadingOverlay(false);
      scrollToTop();
    });
  };

  const onChangeViewStateRequest = (vs: BwjViewState) => {
    if(vs === BwjViewState.Loading) {
      loadingMessage.current = 'One moment please...';
    }
    setViewState(vs);
  }

  return (
    <Elements
      stripe={stripe.current}
      options={{
        mode: 'payment',
        amount: projectConfig.total,
        currency: 'usd',
      }}
    >
      <BwjViewManager
        state={viewState}
        loadingState={{
          message: loadingMessage.current,
        }}
        errorState={errorState}
        customState={{
          message: 'Thank you! Your payment was successful.  You will receive an email shortly with your receipt and your delivery/installation arrangements (be sure to check your Junk folder).',
          graphic: SuccessGraphic,
          graphicPortraitOriented: true,
        }}
      >
        <CheckoutProductModalManager
          projectConfig={projectConfig}
          onChangeViewStateRequest={onChangeViewStateRequest}
        />
        <TermsReviewPanel
          content={termsOfService.current}
          showPanel={showTermsPanel}
          onDismissPanel={onDismissTermsPanel}
        />
        <BwjPageHeader
          titleIconName={AppRoutes.checkout.icon}
          title={'Checkout'}
        />
        <BwjPageContent contentDescription={'primary content (no subcontent)'}>
          <PriceChangeSummary projectConfig={projectConfig} />
          {paymentFailed.current &&
          <div className={cs.errorContainer}>
            <BwjNote
              type={MessageBarType.error}
              textContent={'We&#39;re sorry &ndash; We had a problem processing your payment information. Please try again.'}
              renderTextAsHtml={true}
            />
          </div>}
          {!!paymentError &&
          <div className={cs.errorContainer}>
            <BwjNote
              type={MessageBarType.error}
              textContent={paymentError.message}
              renderTextAsHtml={true}
            />
          </div>}
          <div className={cs.main}>
            <div className={cs.formMain}>
              <SteppedSectionList>
                <PaymentSectionAcceptTerms
                  state={listState.acceptTerms}
                  onEditSection={onEditTermsSection}
                  onAdvanceToNextSection={onAdvanceToInstallDateSection}
                  termsAcceptance={termsAcceptance}
                  onAcceptanceChanged={onAcceptanceChanged}
                  onViewTerms={() => setShowTermsPanel(true)}
                />
                <PaymentSectionInstallDate
                  state={listState.installDate}
                  onEditSection={onEditInstallDateSection}
                  onAdvanceToNextSection={onAdvanceToBillingAddressSection}
                  selectedDate={selectedInstallTime}
                  installOptions={availableInstallTimes.current}
                  onTimeSelected={setSelectedInstallTime}
                />
                <PaymentSectionBillingAddress
                  state={listState.billingAddress}
                  onEditSection={onEditBillingAddressSection}
                  onAdvanceToNextSection={onAdvanceToPaymentInfoSection}
                  onFormChanged={onBillingAddressChanged}
                />
                <PaymentSectionCardInfo
                  state={listState.paymentInfo}
                  onEditSection={onEditCardInfoSection}
                  onFormChanged={onCardInfoChanged}
                  onCompletePayment={onCompletePayment}
                  blockPaymentSubmission={paymentError?.type === PaymentErrorType.Blocking}
                />
              </SteppedSectionList>
              {devMode && <BwjCheckbox
                id={'devSpf'}
                label={'Simulating payment failure'}
                checked={simulatePaymentFailure}
                onChange={setSimulatePaymentFailure}
              />}
            </div>
            <div className={cs.formAdditional}>
              <CheckoutSummary projectConfig={projectConfig} />
            </div>
          </div>
        </BwjPageContent>
      </BwjViewManager>
    </Elements>
  );
};